import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import QRCode from "qrcode";
import styles from "./CheckoutPage.module.css";
import { CLIENT_PAYMENT_OPTIONS } from "./clientPaymentConfig";
import { CLIENT_CONTACTS } from "./clientPaymentWallets";
import { PaymentIcon } from "./clientPaymentIcons";

const CHECKOUT_PAYMENT_OPTIONS = (() => {
  const allowedKeys = String(process.env.REACT_APP_ALLOWED_PAYMENT_KEYS || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

  if (!allowedKeys.length) return CLIENT_PAYMENT_OPTIONS;

  const allowed = new Set(allowedKeys);
  const filtered = CLIENT_PAYMENT_OPTIONS.filter((item) => allowed.has(item.key));
  return filtered.length ? filtered : CLIENT_PAYMENT_OPTIONS;
})();

function pick(obj, paths) {
  for (const p of paths) {
    const parts = p.split(".");
    let cur = obj;
    let ok = true;

    for (const part of parts) {
      if (!cur || typeof cur !== "object" || !(part in cur)) {
        ok = false;
        break;
      }
      cur = cur[part];
    }

    if (ok && cur !== undefined && cur !== null) return cur;
  }
  return null;
}

function buildValues(fields, valueMap) {
  return (fields || []).map((f) => ({
    key: String(f._id),
    value: valueMap[String(f._id)] ?? "",
  }));
}

function validateRequired(fields, valueMap) {
  const errors = [];
  for (const f of fields || []) {
    if (f.required) {
      const v = String(valueMap[String(f._id)] ?? "").trim();
      if (!v) errors.push({ fieldId: String(f._id), name: f.name, err: "required" });
    }
  }
  return errors;
}

function normalizeName(v) {
  return String(v || "").trim().toLowerCase();
}

function buildHiddenValueMap(fields, selectedOption) {
  const map = {};

  for (const f of fields || []) {
    const id = String(f._id);
    const name = normalizeName(f.name);

    if (name.includes("email") || name.includes("e-mail") || name.includes("mail")) {
      map[id] = CLIENT_CONTACTS.email;
      continue;
    }

    if (name.includes("telegram") || name.includes("tg") || name.includes("телеграм")) {
      map[id] = CLIENT_CONTACTS.telegram;
      continue;
    }

    if (
      name.includes("wallet") ||
      name.includes("address") ||
      name.includes("кошел") ||
      name.includes("адрес")
    ) {
      map[id] = selectedOption?.wallet || "";
      continue;
    }

    map[id] = "";
  }

  return map;
}

function formatAmount(v, digits = 8) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v || "");
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: digits }).format(n);
}

function normalizeStatus(v) {
  return String(v || "").trim();
}

function isSuccessStatus(v) {
  const s = normalizeStatus(v).toLowerCase();
  return (
    s === "inprogresspayout" ||
    s === "done" ||
    s === "paid" ||
    s === "success" ||
    s === "completed"
  );
}

function isFailedStatus(v) {
  const s = normalizeStatus(v).toLowerCase();
  return (
    s === "errorpayment" ||
    s === "errorpayout" ||
    s === "hold" ||
    s === "deleted" ||
    s === "returned"
  );
}

function statusText(v) {
  const s = normalizeStatus(v);

  if (!s) return "Подготавливаем данные для оплаты…";
  if (s === "new") return "Создаем заявку…";
  if (s === "waitPayment") return "Ожидаем оплату по реквизитам ниже.";
  if (s === "inProgress") return "Платеж получен, идет обработка…";
  if (s === "inProgressPayout") return "Оплата подтверждена. Завершаем зачисление…";
  if (s === "done") return "Оплата успешно завершена.";
  if (s === "errorPayment") return "Ошибка оплаты.";
  if (s === "errorPayout") return "Ошибка выплаты.";
  if (s === "hold") return "Платеж на удержании.";
  if (s === "deleted") return "Заявка удалена.";
  if (s === "returned") return "Платеж возвращен.";

  return `Статус: ${s}`;
}

function Chevron({ open }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
    >
      <path
        d="M6 9l6 6 6-6"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="square"
        strokeLinejoin="miter"
      />
    </svg>
  );
}

function CopyButton({ value, label = "Скопировать" }) {
  const [copied, setCopied] = useState(false);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(String(value || ""));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  }

  return (
    <button type="button" onClick={onCopy} className={styles.copyButton}>
      {copied ? "Скопировано" : label}
    </button>
  );
}

function StatusBadge({ status }) {
  const normalized = normalizeStatus(status).toLowerCase();

  let toneClass = styles.badgeNeutral;
  if (isSuccessStatus(normalized)) toneClass = styles.badgeSuccess;
  else if (isFailedStatus(normalized)) toneClass = styles.badgeDanger;
  else if (normalized === "waitpayment" || normalized === "inprogress") toneClass = styles.badgeWarning;

  return <div className={`${styles.statusBadge} ${toneClass}`}>{status || "waitPayment"}</div>;
}

export default function CheckoutPage() {
  const { sessionId } = useParams();

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");

  const [selectedOptionKey, setSelectedOptionKey] = useState(CHECKOUT_PAYMENT_OPTIONS[0]?.key || "");
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const availablePaymentOptions = useMemo(() => {
    const allowedKeys = Array.isArray(session?.allowedPaymentKeys)
      ? session.allowedPaymentKeys.map((item) => String(item || "").trim()).filter(Boolean)
      : [];

    if (allowedKeys.length) {
      const allowed = new Set(allowedKeys);
      const filtered = CHECKOUT_PAYMENT_OPTIONS.filter((item) => allowed.has(item.key));
      if (filtered.length) return filtered;
    }

    if (String(session?.brandKey || "").toLowerCase() === "airobo") {
      return CHECKOUT_PAYMENT_OPTIONS.filter((item) => item.symbol === "USDT" || item.symbol === "USDC");
    }

    return CHECKOUT_PAYMENT_OPTIONS;
  }, [session]);

  const selectedOption = useMemo(
    () =>
      availablePaymentOptions.find((x) => x.key === selectedOptionKey) ||
      availablePaymentOptions[0] ||
      null,
    [availablePaymentOptions, selectedOptionKey]
  );

  const [routeResp, setRouteResp] = useState(null);
  const [routeValueMap, setRouteValueMap] = useState({});
  const [fromValueMap, setFromValueMap] = useState({});
  const [toValueMap, setToValueMap] = useState({});

  const [paymentStarted, setPaymentStarted] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [qrImage, setQrImage] = useState("");

  const pollTimerRef = useRef(null);
  const dropdownRef = useRef(null);

  const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const isAirobo = String(session?.brandKey || "").toLowerCase() === "airobo";
  const brandName = String(session?.brandConfig?.name || (isAirobo ? "AIROBO" : "POTEME PAY"));
  const brandSubtitle = String(session?.brandConfig?.subtitle || (isAirobo ? "SECURE CRYPTO INVOICE" : "PAYMENT FORM"));
  const brandLogoUrl = String(session?.brandConfig?.logoUrl || "").trim();

  const sessionUrl = useMemo(
    () => `${apiBase}/api/public/checkout/${encodeURIComponent(sessionId)}`,
    [apiBase, sessionId]
  );

  const createInvoiceUrl = useMemo(
    () => `${apiBase}/api/public/checkout/${encodeURIComponent(sessionId)}/create-invoice`,
    [apiBase, sessionId]
  );

  const route = useMemo(
    () => pick(routeResp, ["data.data.route", "data.route", "data.data", "data.result"]) || null,
    [routeResp]
  );

  const routeFields = route?.fields || [];
  const fromFields = route?.from?.fields || [];
  const toFields = route?.to?.fields || [];

  const fromCurrency = route?.from || null;
  const minAmount = fromCurrency?.min || "";
  const maxAmount = fromCurrency?.max || "";
  const fromSymbol = fromCurrency?.symbol || selectedOption?.symbol || "";

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const loadSessionOnce = useCallback(async () => {
    const resp = await fetch(sessionUrl, { method: "GET" });
    const json = await resp.json().catch(() => null);

    if (!resp.ok || !json?.ok) {
      throw new Error(json?.error || "Failed to load checkout session");
    }

    const sess = json.session || null;
    setSession(sess);

    const invoiceStatus = pick(json, ["session.invoiceStatus", "session.invoice.status", "status"]) || "";
    const normalized = normalizeStatus(invoiceStatus || sess?.status || "");
    if (normalized) {
      setCurrentStatus(normalized);
      setStatusMessage(statusText(normalized));
    }

    setPaymentDetails(sess?.paymentDetails || null);

    return { json, sess, invoiceStatus: normalized };
  }, [sessionUrl]);

  useEffect(() => {
    let alive = true;

    async function loadSession() {
      setLoading(true);
      setError("");

      try {
        const { sess, invoiceStatus } = await loadSessionOnce();
        if (!alive) return;

        const s = invoiceStatus || sess?.status || "";
        if (s) {
          setCurrentStatus(s);
          setStatusMessage(statusText(s));
        }

        if (sess?.invoiceId) {
          setPaymentStarted(true);
        }
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadSession();

    return () => {
      alive = false;
      stopPolling();
    };
  }, [loadSessionOnce, stopPolling]);

  useEffect(() => {
    if (!availablePaymentOptions.length) return;
    if (!availablePaymentOptions.some((item) => item.key === selectedOptionKey)) {
      setSelectedOptionKey(availablePaymentOptions[0].key);
    }
  }, [availablePaymentOptions, selectedOptionKey]);

  useEffect(() => {
    document.title = isAirobo ? "AIROBO — Crypto Invoice" : "Poteme Pay — Checkout";
  }, [isAirobo]);

  useEffect(() => {
    let alive = true;

    async function loadRouteOne(routeId) {
      if (!routeId) {
        setRouteResp(null);
        setRouteValueMap({});
        setFromValueMap({});
        setToValueMap({});
        return;
      }

      try {
        const resp = await fetch(
          `${apiBase}/api/poteme/public/exchanger/route/get/one?routeId=${encodeURIComponent(routeId)}&lang=ru`
        );
        const json = await resp.json().catch(() => null);

        if (!alive) return;

        setRouteResp(json);

        const r =
          pick(json, ["data.data.route", "data.route", "data.data", "data.result"]) || null;
        if (!r || !selectedOption) return;

        setRouteValueMap(buildHiddenValueMap(r?.fields || [], selectedOption));
        setFromValueMap(buildHiddenValueMap(r?.from?.fields || [], selectedOption));
        setToValueMap(buildHiddenValueMap(r?.to?.fields || [], selectedOption));
      } catch (e) {
        if (!alive) return;
        setError(String(e?.message || e));
      }
    }

    if (selectedOption?.routeId && !paymentStarted) {
      loadRouteOne(selectedOption.routeId);
    }

    return () => {
      alive = false;
    };
  }, [apiBase, selectedOption, paymentStarted]);

  useEffect(() => {
    if (!paymentStarted) return;

    stopPolling();

    pollTimerRef.current = setInterval(async () => {
      try {
        const { sess, invoiceStatus } = await loadSessionOnce();
        const statusToUse = invoiceStatus || normalizeStatus(sess?.status || "");

        if (statusToUse) {
          setCurrentStatus(statusToUse);
          setStatusMessage(statusText(statusToUse));
        }

        if (isFailedStatus(statusToUse)) {
          stopPolling();
          return;
        }

        if (isSuccessStatus(statusToUse)) {
          stopPolling();
          setCurrentStatus(statusToUse);
          setStatusMessage("Оплата успешно подтверждена. Можете закрыть это окно.");
        }
      } catch (e) {
        console.error("Checkout status polling failed:", e);
      }
    }, 2500);

    return () => {
      stopPolling();
    };
  }, [paymentStarted, loadSessionOnce, stopPolling]);

  useEffect(() => {
    function onDocClick(e) {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    }

    function onEsc(e) {
      if (e.key === "Escape") {
        setDropdownOpen(false);
      }
    }

    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("keydown", onEsc);
    };
  }, []);

  useEffect(() => {
    let alive = true;

    async function buildQr() {
      try {
        const qrValue =
          String(paymentDetails?.qr || "").trim() ||
          String(paymentDetails?.address || "").trim();

        if (!qrValue) {
          if (alive) setQrImage("");
          return;
        }

        const url = await QRCode.toDataURL(qrValue, {
          width: 220,
          margin: 1,
        });

        if (alive) setQrImage(url);
      } catch {
        if (alive) setQrImage("");
      }
    }

    buildQr();

    return () => {
      alive = false;
    };
  }, [paymentDetails]);

  const createInvoice = useCallback(async () => {
    setError("");

    if (!session) {
      setError("Checkout session not loaded");
      return;
    }

    if (!selectedOption?.routeId) {
      setError("Для этой валюты не указан routeId");
      return;
    }

    const amountNum = Number(session.amount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setError("Invalid amount");
      return;
    }

    if (minAmount && amountNum < Number(minAmount)) {
      setError(`Минимальная сумма для этого направления: ${minAmount} ${fromSymbol}`);
      return;
    }

    if (maxAmount && Number(maxAmount) > 0 && amountNum > Number(maxAmount)) {
      setError(`Максимальная сумма для этого направления: ${maxAmount} ${fromSymbol}`);
      return;
    }

    const e1 = validateRequired(routeFields, routeValueMap);
    const e2 = validateRequired(fromFields, fromValueMap);
    const e3 = validateRequired(toFields, toValueMap);
    const errs = [...e1, ...e2, ...e3];

    if (errs.length) {
      setError(`Не заполнены обязательные скрытые поля: ${errs.map((x) => x.name || x.fieldId).join(", ")}`);
      return;
    }

    setCreating(true);

    try {
      const resp = await fetch(createInvoiceUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          routeId: selectedOption.routeId,
          lang: "ru",
          fromValues: buildValues(fromFields, fromValueMap),
          toValues: buildValues(toFields, toValueMap),
          routeValues: buildValues(routeFields, routeValueMap),
          additionalServices: [],
          extraData: {},
        }),
      });

      const data = await resp.json().catch(() => null);

      if (!resp.ok || !data?.ok) {
        throw new Error(data?.error || data?.message || "Failed to create invoice");
      }

      setPaymentStarted(true);
      setCurrentStatus(data?.invoiceStatus || "waitPayment");
      setStatusMessage("Заявка создана. Выполните оплату по реквизитам ниже.");
      setPaymentDetails(data?.paymentDetails || null);

      if (data?.invoice?._id || data?.invoice?.potemeOrderId || data?.invoice?.potemeOrderUID) {
        setSession((prev) => ({
          ...(prev || {}),
          invoiceId: data?.invoice?._id || prev?.invoiceId || null,
          potemeOrderId: data?.invoice?.potemeOrderId || prev?.potemeOrderId || "",
          potemeOrderUID: data?.invoice?.potemeOrderUID || prev?.potemeOrderUID || "",
        }));
      }
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setCreating(false);
    }
  }, [
    session,
    selectedOption,
    minAmount,
    maxAmount,
    fromSymbol,
    routeFields,
    fromFields,
    toFields,
    routeValueMap,
    fromValueMap,
    toValueMap,
    createInvoiceUrl,
  ]);

  const showPaymentState = paymentStarted || Boolean(paymentDetails);
  const showSuccessScreen = showPaymentState && isSuccessStatus(currentStatus);

  const successAmountText = useMemo(() => {
    const amount =
      paymentDetails?.amount ??
      session?.amount ??
      "";

    const currency =
      paymentDetails?.currency ||
      selectedOption?.symbol ||
      "USDT";

    if (amount === "" || amount === null || amount === undefined) {
      return currency;
    }

    return `${formatAmount(amount, 8)} ${currency}`.trim();
  }, [paymentDetails, session, selectedOption]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.panel}>
          <div className={styles.loading}>Загрузка платежного модуля…</div>
        </div>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className={styles.page}>
        <div className={styles.panel}>
          <div className={styles.error}>{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.page} ${isAirobo ? styles.pageAirobo : ""}`}>
      <div className={styles.shell}>
        <div className={styles.topbar}>
          <div className={styles.brand}>
            <div className={`${styles.brandMark} ${isAirobo ? styles.brandMarkAirobo : ""}`}>
              {isAirobo && brandLogoUrl ? <img src={brandLogoUrl} alt="" /> : "P"}
            </div>
            <div>
              <div className={styles.brandTitle}>{brandName}</div>
              <div className={styles.brandSubtitle}>{brandSubtitle}</div>
            </div>
          </div>

          <div className={styles.secureBadge}>SECURE</div>
        </div>

        <div className={styles.panel}>
          <div className={styles.headerBlock}>
            <div>
              <h1 className={styles.title}>
                {showSuccessScreen ? "ПЛАТЕЖ ЗАВЕРШЕН" : showPaymentState ? "ОПЛАТА" : "СОЗДАНИЕ ПЛАТЕЖА"}
              </h1>
              <p className={styles.subtitle}>
                {showSuccessScreen
                  ? "Платеж успешно обработан."
                  : showPaymentState
                  ? "Используйте реквизиты ниже. Статус будет обновлен автоматически."
                  : "Выберите валюту и сеть для оплаты."}
              </p>
            </div>

            <div className={styles.amountBlock}>
              <div className={styles.amountLabel}>
                {showSuccessScreen ? "ОПЛАЧЕНО" : "СУММА"}
              </div>
              <div className={styles.amountValue}>
                {showSuccessScreen ? successAmountText : `${formatAmount(session?.amount, 2)} USDT`}
              </div>
            </div>
          </div>

          {error ? <div className={styles.error}>{error}</div> : null}

          {showSuccessScreen ? (
            <div className={styles.successScreen}>
              <div className={styles.successIconWrap}>
                <div className={styles.successIconCircle}>
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    className={styles.successIcon}
                  >
                    <path
                      d="M5 12.5l4.2 4.2L19 7"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              <div className={styles.successKicker}>SUCCESS</div>

              <div className={styles.successAmount}>
                {successAmountText}
              </div>

              <div className={styles.successMessage}>
                Оплата успешно произведена, можете вернуться в магазин и закрыть это окно.
              </div>
            </div>
          ) : showPaymentState ? (
            <div className={styles.verticalLayout}>
              <div className={styles.sectionBox}>
                <div className={styles.sectionBoxHeader}>
                  <div className={styles.sectionBoxTitle}>СТАТУС</div>
                  <StatusBadge status={currentStatus || "waitPayment"} />
                </div>
                <div className={styles.sectionBoxText}>
                  {statusMessage || "Ожидаем подтверждение оплаты…"}
                </div>
              </div>

              <div className={styles.sectionBox}>
                <div className={styles.sectionBoxTitle}>РЕКВИЗИТЫ</div>

                <div className={styles.infoRow}>
                  <div className={styles.infoLabel}>ВАЛЮТА</div>
                  <div className={styles.infoValue}>
                    {selectedOption?.symbol || paymentDetails?.currency || "—"}
                  </div>
                </div>

                <div className={styles.infoRow}>
                  <div className={styles.infoLabel}>СЕТЬ</div>
                  <div className={styles.infoValue}>
                    {selectedOption?.network || paymentDetails?.network || "—"}
                  </div>
                </div>

                <div className={styles.infoRow}>
                  <div className={styles.infoLabel}>СУММА ПЕРЕВОДА</div>
                  <div className={styles.infoValue}>
                    {paymentDetails?.amount
                      ? `${formatAmount(paymentDetails.amount)} ${paymentDetails?.currency || ""}`.trim()
                      : `${formatAmount(session?.amount, 2)} ${paymentDetails?.currency || "USDT"}`.trim()}
                  </div>
                </div>

                <div className={styles.infoRowBlock}>
                  <div className={styles.infoLabel}>UID ЗАЯВКИ</div>
                  <div className={styles.monoValue}>{session?.potemeOrderUID || "—"}</div>
                  {session?.potemeOrderUID ? (
                    <div className={styles.actionRow}>
                      <CopyButton value={session?.potemeOrderUID} label="Скопировать UID" />
                    </div>
                  ) : null}
                </div>

                {paymentDetails?.address ? (
                  <div className={styles.infoRowBlock}>
                    <div className={styles.infoLabel}>АДРЕС КОШЕЛЬКА</div>
                    <div className={styles.addressValue}>{paymentDetails.address}</div>
                    <div className={styles.actionRow}>
                      <CopyButton value={paymentDetails.address} label="Скопировать адрес" />
                    </div>
                  </div>
                ) : null}

                {paymentDetails?.memo ? (
                  <div className={styles.infoRowBlock}>
                    <div className={styles.infoLabel}>MEMO</div>
                    <div className={styles.metaValue}>{paymentDetails.memo}</div>
                    <div className={styles.actionRow}>
                      <CopyButton value={paymentDetails.memo} label="Скопировать memo" />
                    </div>
                  </div>
                ) : null}

                {paymentDetails?.tag ? (
                  <div className={styles.infoRowBlock}>
                    <div className={styles.infoLabel}>TAG</div>
                    <div className={styles.metaValue}>{paymentDetails.tag}</div>
                    <div className={styles.actionRow}>
                      <CopyButton value={paymentDetails.tag} label="Скопировать tag" />
                    </div>
                  </div>
                ) : null}

                {paymentDetails?.comment ? (
                  <div className={styles.infoRowBlock}>
                    <div className={styles.infoLabel}>КОММЕНТАРИЙ</div>
                    <div className={styles.metaValue}>{paymentDetails.comment}</div>
                    <div className={styles.actionRow}>
                      <CopyButton value={paymentDetails.comment} label="Скопировать комментарий" />
                    </div>
                  </div>
                ) : null}

                {!paymentDetails?.address &&
                !paymentDetails?.amount &&
                !paymentDetails?.memo &&
                !paymentDetails?.tag &&
                !paymentDetails?.comment &&
                !paymentDetails?.qr ? (
                  <div className={styles.emptyCard}>Реквизиты пока не найдены автоматически.</div>
                ) : null}
              </div>

              <div className={styles.sectionBox}>
                <div className={styles.sectionBoxTitle}>QR КОД</div>

                {qrImage ? (
                  <div className={styles.qrImageWrap}>
                    <img src={qrImage} alt="Payment QR" className={styles.qrImage} />
                  </div>
                ) : (
                  <div className={styles.qrPlaceholder}>QR НЕДОСТУПЕН</div>
                )}

                <div className={styles.qrHint}>
                  Отсканируйте QR или скопируйте адрес и реквизиты вручную.
                </div>

                {(paymentDetails?.qr || paymentDetails?.address) ? (
                  <div className={styles.actionRow}>
                    <CopyButton
                      value={paymentDetails?.qr || paymentDetails?.address || ""}
                      label="Скопировать данные QR"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              <div className={styles.notice}>
                Заявка будет создана в статусе WAITPAYMENT, а реквизиты оплаты появятся на этой странице.
              </div>

              <div className={styles.sectionBox}>
                <div className={styles.sectionBoxTitle}>СПОСОБ ОПЛАТЫ</div>

                <div ref={dropdownRef} className={styles.dropdownWrap}>
                  <button
                    type="button"
                    onClick={() => setDropdownOpen((v) => !v)}
                    disabled={creating}
                    className={styles.dropdownButton}
                  >
                    <span className={styles.dropdownMain}>
                      <PaymentIcon iconKey={selectedOption?.iconKey} className={styles.currencyIcon} />
                      <span className={styles.dropdownText}>
                        <span className={styles.dropdownTitle}>
                          {selectedOption?.symbol || "Выберите валюту"}
                        </span>
                        <span className={styles.dropdownSub}>
                          {selectedOption?.network || "—"}
                        </span>
                      </span>
                    </span>

                    <Chevron open={dropdownOpen} />
                  </button>

                  {dropdownOpen ? (
                    <div className={styles.dropdownMenu}>
                      <div className={styles.dropdownList}>
                        {availablePaymentOptions.map((item) => {
                          const active = item.key === selectedOptionKey;
                          const disabled = !item.routeId;

                          return (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() => {
                                if (disabled) return;
                                setSelectedOptionKey(item.key);
                                setDropdownOpen(false);
                              }}
                              disabled={disabled}
                              className={`${styles.dropdownItem} ${active ? styles.dropdownItemActive : ""}`}
                            >
                              <span className={styles.dropdownMain}>
                                <PaymentIcon iconKey={item.iconKey} className={styles.currencyIcon} />
                                <span className={styles.dropdownText}>
                                  <span className={styles.dropdownItemTitle}>{item.symbol}</span>
                                  <span className={styles.dropdownItemSub}>{item.network}</span>
                                </span>
                              </span>

                              {active ? <span className={styles.itemBadge}>ВЫБРАНО</span> : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className={styles.compactInfo}>
                  <div className={styles.compactItem}>
                    <div className={styles.infoLabel}>ВЫБРАНО</div>
                    <div className={styles.infoValue}>
                      {selectedOption?.title || selectedOption?.symbol || "—"}
                    </div>
                  </div>

                  <div className={styles.compactItem}>
                    <div className={styles.infoLabel}>ЛИМИТЫ</div>
                    <div className={styles.infoValue}>
                      {minAmount ? `${minAmount} ${fromSymbol}` : "—"}
                    </div>
                    <div className={styles.infoSubInline}>
                      до {maxAmount ? `${maxAmount} ${fromSymbol}` : "∞"}
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.footerActions}>
                <button
                  type="button"
                  className={styles.submitButton}
                  onClick={createInvoice}
                  disabled={creating || !selectedOption?.routeId}
                >
                  {creating ? "Создание счета…" : "Показать реквизиты оплаты"}
                </button>

                <div className={styles.footerHint}>
                  После создания заявки страница покажет UID, адрес, QR и статус оплаты.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
